import type { FC } from 'react';
import { useEffect } from 'react';
import React from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { OnboardingComplete } from '@pages/common/Onboarding/components/OnboardingComplete/OnboardingComplete';
import { MAX_RATE_COMPLETE } from '@pages/common/Onboarding/const/max-rate-complete';
import { getOnboardingCompleteSelector } from '@pages/common/Onboarding/store/onboarding-selectors';
import { CrossIcon } from '@setup/svg-icons/svg-icons';
import { AnalyticsEventEnum } from '@shared/enums/analyticsEvent.enum';
import { ProfileViewDataEnum } from '@shared/enums/profileViewData.enum';
import useAnalyticsEventTracker from '@shared/hooks/analytics/useAnalyticsEventTracker';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { OnboardingPopoverStyle } from '@shared/ui/popovers/OnboardingPopover/OnboardingPopover.style';
import BookDemoBlock from '@shared/ui/popovers/OnboardingPopover/components/BookDemoBlock/BookDemoBlock';
import { OnboaridngItems } from '@shared/ui/popovers/OnboardingPopover/components/OnboaridngItems/OnboaridngItems';
import { SkipBlock } from '@shared/ui/popovers/OnboardingPopover/components/SkipBlock/SkipBlock';
import { MuiIconButton } from '@shared/ui-kit';
import { setViewDataItem } from '@store/Profile/profile.actions';

type OnboardingPopoverProps = {
  children: React.ReactNode;
  anchorOriginVertical?: 'bottom' | 'top' | 'center';
  anchorOriginHorizontal?: 'center' | 'right' | 'left';
  transformOriginVertical?: 'bottom' | 'top' | 'center';
  transformOriginHorizontal?: 'center' | 'right' | 'left';
};
const OnboardingPopover: FC<OnboardingPopoverProps> = ({
  children,
  anchorOriginHorizontal = 'right',
  anchorOriginVertical = 'bottom',
  transformOriginHorizontal = 'right',
  transformOriginVertical = 'top',
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'onboardingPopover',
  });
  const onboardingCompleteRate = useAppSelector(getOnboardingCompleteSelector);
  const gaEventTracker = useAnalyticsEventTracker();

  const isCompeteOnboarding = onboardingCompleteRate >= MAX_RATE_COMPLETE;

  useEffect(() => {
    if (popupState?.isOpen) {
      gaEventTracker({
        event: AnalyticsEventEnum.onboardingChecklistSelected,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupState?.isOpen]);
  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Box
        sx={{ display: 'inline-block', cursor: 'pointer' }}
        id="onboaridngChecklistsPopover"
        {...bindTrigger(popupState)}
      >
        {children}
      </Box>
      <OnboardingPopoverStyle
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: anchorOriginVertical,
          horizontal: anchorOriginHorizontal,
        }}
        transformOrigin={{
          vertical: transformOriginVertical,
          horizontal: transformOriginHorizontal,
        }}
        classes={{
          paper: 'onboarding-checklists-popover-container',
        }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                backgroundColor: 'inherit',
                transform: 'translateY(-50%) rotate(45deg)',
                boxShadow: '-3px -3px 5px -2px rgba(0,0,0,0.1)',
              },
            },
          },
        }}
      >
        <Box
          sx={{ visibility: 'hidden' }}
          id={'onboaridngChecklistsPopoverCloseTrigger'}
          onClick={popupState.close}
        />
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ color: theme.palette.case.neutral.n900 }} variant={'s1'}>
            Set up your Hubmee!
          </Typography>

          <MuiIconButton
            onClick={() => {
              popupState.close();
              gaEventTracker({
                event: AnalyticsEventEnum.onboardingChecklistCloseSelected,
              });
            }}
            size="small"
          >
            <CrossIcon />
          </MuiIconButton>
        </Box>
        <Box mt={'4px'}>
          <Box display="flex" justifyContent="flex-end" mb="8px">
            <Typography color={(theme) => theme.palette.case.neutral.n700} variant="t14r">
              {`${onboardingCompleteRate || 0}%`}
            </Typography>
          </Box>
          <LinearProgress sx={{ width: '100%' }} variant="determinate" value={onboardingCompleteRate || 0} />
        </Box>
        {isCompeteOnboarding ? (
          <OnboardingComplete
            closeModal={() => {
              dispatch(setViewDataItem({ entity: ProfileViewDataEnum.is_view_skip_onboarding }));
              popupState.close();
              gaEventTracker({
                event: AnalyticsEventEnum.onboardingChecklistPassed,
              });
            }}
          />
        ) : (
          <>
            <Box
              sx={{
                p: '16px 0px',
                m: '16px 0px',
                borderTop: `1px solid ${theme.palette.case.neutral.n200}`,
                borderBottom: `1px solid ${theme.palette.case.neutral.n200}`,
              }}
            >
              <OnboaridngItems onClose={() => popupState.close()} />
            </Box>
            <BookDemoBlock />
            <SkipBlock closeCallback={() => popupState.close()} />
          </>
        )}
      </OnboardingPopoverStyle>
    </Box>
  );
};

export default OnboardingPopover;
